import buildMessages from "./message-builder";
import buildMessageDetails from "./message-detail-builder";
import buildConditionalsWithAgreement from "./conditionals-with-agreement-builder";
import buildConsenters from "./consenter-builder";
import buildDoctors from "./doctor-builder";
import { getStates } from "./location";
import { buildPharmacies } from "./pharmacy-builder";
import { buildImageList } from "./image-list-builder";
import { buildVisitList } from "./visit-list-builder";
import { removeParam } from "./fn";
import buildInvoices from "./invoice-builder";
import { FILTER_OPTIONS_FIELD } from "./constants";

const patchField = (fieldName, patchOrPatchBuilder = {}, fields = []) => {
  const fieldIndex = fields.findIndex(field => field.name === fieldName);
  if (fieldIndex !== -1) {
    const field = fields[fieldIndex];
    return [
      ...fields.slice(0, fieldIndex),
      {
        ...field,
        ...(typeof patchOrPatchBuilder === "object"
          ? patchOrPatchBuilder
          : patchOrPatchBuilder(field))
      },
      ...fields.slice(fieldIndex + 1)
    ];
  } else {
    return fields;
  }
};

const deduplicateSeparatorFields = (fields = []) => {
  const newFields = fields.filter(
    (field, i) =>
      field.type !== "bar" || (i > 0 && fields[i - 1].type !== "bar")
  );
  // remove trailing separator
  if (newFields.length > 0 && newFields[newFields.length - 1].type === "bar") {
    newFields.splice(-1);
  }
  return newFields;
};

const deduplicateSeparators = screen => {
  const { fields, footerFields } = screen;
  const newFields = deduplicateSeparatorFields(fields);
  const newFooterFields = deduplicateSeparatorFields(footerFields);
  // put a separator back if there was at least one separator between 'fields' and 'footerFields' originally
  if (newFields.length > 0 && newFooterFields.length > 0) {
    if (fields[fields.length - 1].type === "bar") {
      newFields.push(fields[fields.length - 1]);
    }
    if (footerFields[0].type === "bar") {
      newFields.push(footerFields[0]);
    }
  }
  return {
    ...screen,
    fields: newFields,
    footerFields: newFooterFields
  };
};

const internationalizedField = (field, translations) => {
  if (field.name === "communication_method" && field.text) {
    // port of the internationalization approach used in iOS native and Android native
    // "Phone", "Video", "Message Center"
    const translationKey = field.text
      .split(" ")
      .map(text => text.toLowerCase())
      .join("_");

    const internationalized = translations[translationKey] || field.text;
    return { ...field, text: internationalized };
  }

  return field;
};

const mapData = (data, settings) => {
  if (!data.screens) {
    return { ...data, screens: [] };
  }
  const mappedScreens = data.screens.map((rawScreen, index, screens) => {
    const screen = deduplicateSeparators(rawScreen);
    // preserve hidden fields as is
    screen.hiddenFields = rawScreen.hiddenFields;
    switch (screen.osController.iOS) {
      case "BaseViewController":
        const fields =
          screen.identifier === "ReferralsFormProviderInfo" ||
          screen.identifier === "ReferralsFormInsuranceInfo"
            ? screen.fields.map(field => {
                if (field.name === "referrals_specialties.name") {
                  return {
                    ...field,
                    options: [
                      { value: "", text: "" },
                      ...screen.data[0]["referrals_specialties.name"]
                    ]
                  };
                }
                if (field.name === "insurance_plan.name") {
                  return {
                    ...field,
                    options: [
                      { value: "", text: "" },
                      ...screen.data[0]["insurance_plan.name"]
                    ]
                  };
                }
                return field;
              })
            : screen.fields.map(field =>
                (field.name === "health_category" ||
                  field.name === "member_id") &&
                field.action &&
                field.action.type === "alert"
                  ? {
                      ...field,
                      action: {
                        ...field.action,
                        title: settings.translations.sorry_text
                      }
                    }
                  : // Temporary fix
                  // ToDo: remove after WBSDK-289 will be done
                  field.name === "name_search"
                  ? {
                      ...field,
                      params: field.params.replace("TDFieldOptionSearch", "")
                    }
                  : field
              );
        const footerFields = screen.footerFields.map(field =>
          field.name === "see_all_providers_button" || field.title === "No"
            ? {
                ...field,
                params: `${field.params}|TDFieldOptionSecondaryAction`
              }
            : field
        );
        const conditionals = buildConditionalsWithAgreement(screen);
        return {
          ...screen,
          fields,
          conditionals,
          footerFields
        };
      case "ConsultSearchViewController":
        return {
          ...screen,
          fields: [...screen.fields].map(field => {
            if (!field.data?.search_data?.length) {
              return field;
            }
            if (field.name !== "country_cd") {
              return {
                ...field,
                name: "state",
                options: [
                  { value: "", text: "" },
                  {
                    value: "currentLocation",
                    text: settings.translations.from_current_location
                  },
                  ...(field.data && field.data.search_data
                    ? field.data.search_data
                    : getStates(settings))
                ]
              };
            } else {
              return {
                ...field,
                options: field.data.search_data
              };
            }
          })
        };
      case "FilterViewController":
        if (screen.data && screen.data.filter_data) {
          const options = screen.data.filter_data.reduce(
            (acc, val) =>
              acc.concat(
                val.filter_options.map(dataItem => dataItem.cell_data)
              ),
            []
          );

          const filterOptionsField = {
            name: FILTER_OPTIONS_FIELD.name,
            type: FILTER_OPTIONS_FIELD.type,
            optionsData: screen.data.filter_data,
            options,
            data: { actionEventStatesTags: "TAGClearInput" }
          };

          return {
            ...screen,
            fields: [...screen.fields, filterOptionsField]
          };
        }

        return screen;
      case "CareTeamProfileDetailController":
      case "DetailViewController":
        const extendedFields = [];
        let iterationDiff = 0;
        const fieldsToMap = screen.fields.filter(
          f => f.name !== "available_now"
        );
        fieldsToMap.forEach((field, i) => {
          extendedFields.splice(i + iterationDiff, 0, field);

          if (
            field.name !== "profile_image" &&
            field.name !== "name" &&
            field.name !== "delete_care_team_relation_button" &&
            !field.name.includes("_info")
          ) {
            const descriptionField = {
              type: "text",
              name: `${field.name}_info`,
              title: ""
            };
            extendedFields.splice(i + 1 + iterationDiff, 0, descriptionField);
            iterationDiff += 1;
          }
        });
        return {
          ...screen,
          fields: [...extendedFields]
        };
      case "MessageCenterListViewController":
        const messageList = buildMessages(screen.data);
        return { ...screen, fields: [...messageList] };
      case "MessageCenterDetailViewController":
        const messageDetails = buildMessageDetails(screen.data);
        return { ...screen, fields: [...screen.fields, ...messageDetails] };
      case "ConsultationConfirmationViewController":
        const patchStartOverButton = field => ({
          ...field,
          action: {
            ...field.action,
            data: {
              alert_title: settings.translations.start_over,
              alert_message: settings.translations.will_be_sent_all_way_back,
              button_title: settings.translations.okay
            }
          },
          __noValidation: true
        });

        return {
          ...screen,
          fields: patchField(
            "startOverButton",
            patchStartOverButton,
            screen.fields.map(field =>
              internationalizedField(field, settings.translations)
            )
          )
        };
      case "SearchTreatmentViewController":
        const treatmentField = screen.fields.find(
          field => field.name === "term"
        );
        treatmentField.options = [
          {
            label: "",
            value: ""
          }
        ];
        // eslint-disable-next-line no-underscore-dangle
        treatmentField.__allowManual = true;
        return {
          ...screen,
          fields: screen.fields
        };
      case "AddTreatmentViewController":
        return {
          ...screen,
          action: {
            type: "screen",
            value: screens.find(scr => scr.name === "ConsultationDermIntake")
              .identifier
          }
        };
      case "AuthorizedConsentersListViewController":
        const consentersList = buildConsenters(screen.data);
        const existingFieldsWithConsenters =
          screen.fields.concat(consentersList);
        return { ...screen, fields: existingFieldsWithConsenters };
      case "VideoRoomViewController":
        const updatedScreen = { ...screen };
        updatedScreen.childScreens[0].fields.map(field => {
          if (field.name === "tipTitle") {
            // eslint-disable-next-line no-param-reassign
            field.action = {};
          } else if (
            field.name === "videoTips" ||
            field.name === "videoTipOptions"
          ) {
            // eslint-disable-next-line no-param-reassign
            field.params += "|show-hide-element-web-sdk";
          } else if (field.name === "consultStateText") {
            // eslint-disable-next-line no-param-reassign
            field.title = settings.translations.loading_visit_status;
          }

          return field;
        });
        return updatedScreen;
      case "DoctorsListViewController":
        const addDoctorButton = {
          type: "text",
          title: screen.data.footer_text || settings.translations.add_Doctor,
          action: {
            type: "screen",
            value: "DoctorNew"
          }
        };
        const doctorList = buildDoctors(screen.data);
        if (screen.data?.doctors?.length) {
          return { ...screen, fields: [addDoctorButton, ...doctorList] };
        } else {
          return {
            ...screen,
            fields: [
              addDoctorButton,
              ...patchField(
                "empty_list_message",
                f => removeParam(f, "TDFieldOptionIgnore"),
                screen.fields
              )
            ]
          };
        }
      case "PharmacyListViewController":
        const addPharmacyButton = {
          type: "text",
          title: screen.data.footer_text || settings.translations.add_pharmacy,
          action: {
            type: "screen",
            value: "PharmacyAddSearch"
          }
        };
        const pharmacyList = buildPharmacies(screen.data);
        if (screen.data?.pharmacies?.length) {
          return { ...screen, fields: [addPharmacyButton, ...pharmacyList] };
        } else {
          return {
            ...screen,
            fields: [
              addPharmacyButton,
              ...patchField(
                "empty_list_message",
                f => removeParam(f, "TDFieldOptionIgnore"),
                screen.fields
              )
            ]
          };
        }
      case "MedicalHistoryViewController":
        const addMedicineLinkIndex = screen.fields.findIndex(
          field => field.name === "addMedicineLink"
        );
        const addAllergyLinkIndex = screen.fields.findIndex(
          field => field.name === "addAllergyLink"
        );
        const newFields = [...screen.fields];
        newFields.splice(addMedicineLinkIndex, 1, {
          ...screen.fields[addMedicineLinkIndex],
          action: {
            type: "screen",
            value: "SearchMedicationModal"
          }
        });
        newFields.splice(addAllergyLinkIndex, 1, {
          ...screen.fields[addAllergyLinkIndex],
          action: {
            type: "screen",
            value: ""
          }
        });
        return {
          ...screen,
          fields: newFields
        };
      case "SearchMedicationViewController":
      case "SearchAllergyViewController":
        return {
          ...screen,
          fields: [
            {
              ...screen.fields[0],
              options: [{ label: "", value: "" }]
            }
          ]
        };
      case "ImagesListViewController":
        if ((screen.params || "").includes("TDScreenOptionSkipImagesBuild")) {
          return screen;
        }
        return {
          ...screen,
          fields: [
            {
              type: "text",
              title: screen.data.footer_text,
              action: {
                type: "screen",
                value: "PhotoModal"
              }
            },
            ...screen.fields,
            ...buildImageList(screen.data.images)
          ]
        };
      case "ConsultationListViewController":
        const speakToDoctorButton = {
          name: "speakToDoctorButton",
          type: "text",
          title: settings.translations.speak_to_doctor,
          action: {
            type: "url",
            value: "/screens/request_consult/start"
          }
        };
        if (screen.data?.consultations?.length) {
          return {
            ...screen,
            fields: [
              speakToDoctorButton,
              ...screen.fields,
              ...buildVisitList(screen.data.consultations)
            ]
          };
        } else {
          return {
            ...screen,
            fields: [speakToDoctorButton, ...screen.fields]
          };
        }
      case "AddMedicationViewController":
      case "AddAllergyViewController":
        return {
          ...screen,
          action: { needsValidation: true }
        };
      case "CreditCardController":
        const mappedFields = screen.fields.map(field =>
          field.name === "cc_info.exp_year"
            ? {
                ...field,
                params: `${field.params}|TDFieldOptionExpirationDate`
              }
            : field
        );
        return {
          ...screen,
          fields: mappedFields
        };
      case "ConsultationImagesPcp":
        const changedFields = screen.fields.map(field =>
          field.name === "provider_id"
            ? {
                ...field,
                options: field.options.map(opt =>
                  opt.text === settings.translations.next_available
                    ? { ...opt, value: opt.text }
                    : opt
                )
              }
            : field
        );
        return {
          ...screen,
          fields: changedFields
        };
      case "PharmacyMapViewController":
        const hasManualPharmacy = screen?.fields?.find(f =>
          f.title.includes("Add Pharmacy Manually")
        );
        const manualPharmacyField = hasManualPharmacy
          ? []
          : [
              {
                type: "bar",
                params: "TDFieldOptionAutoCorrectNone"
              },
              {
                name: "__manualPharmacy",
                type: "text",
                params: "TDFieldOptionBold|TDFieldOptionAutoCorrectNone",
                title: settings.translations.add_pharmacy_manually,
                action: {
                  type: "modal_screen",
                  value: "PharmacyAddManually"
                }
              }
            ];
        return {
          ...screen,
          title: settings.translations.pharmacies,
          footerFields: [...screen.footerFields, ...manualPharmacyField]
        };
      case "AccountInvoicesListViewController":
        const invoicesList = buildInvoices(screen.data);
        return { ...screen, fields: [...invoicesList] };
      default:
        return screen;
    }
  });
  return { ...data, screens: mappedScreens };
};

export default mapData;
